import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../api';
import { computeADG, formatADG } from '../utils/format';

const emptyForm = () => ({ weight_kg: '', date: new Date().toISOString().slice(0, 10), weight_type: 'periodic' });

export default function WeightTab({ sheepId }) {
  const [weights, setWeights] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const load = () => {
    setError('');
    api.listWeights(sheepId).then(setWeights).catch(err => setError(err.message));
  };

  useEffect(() => { load(); }, [sheepId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const startAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(v => !v);
  };

  const startEdit = (weight) => {
    setEditing(weight);
    setForm({
      weight_kg: weight.weight_kg,
      date: weight.date,
      weight_type: weight.weight_type || 'periodic',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleSubmit = async () => {
    if (!form.weight_kg) {
      setError('Weight is required.');
      return;
    }
    try {
      const payload = { ...form, weight_kg: parseFloat(form.weight_kg) };
      editing ? await api.updateWeight(editing.id, payload) : await api.addWeight(sheepId, payload);
      closeForm();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteWeight(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const chartData = weights.map(w => ({ date: w.date, kg: w.weight_kg }));
  const adg = computeADG(weights);

  return (
    <div>
      {error && <div className="alert alert-danger mb-4">{error}</div>}

      <div className="section-header">
        <span className="section-title">⚖️ Weight Records</span>
        <button className="btn btn-secondary btn-sm" onClick={startAdd}>+ Add</button>
      </div>

      {adg && (
        <div className="summary-stats mb-4">
          <div className="stat-card" style={{ flex: '1', minWidth: 120 }}>
            <div className="stat-value">{adg.latest} kg</div>
            <div className="stat-label">Latest Weight</div>
          </div>
          <div className="stat-card" style={{ flex: '1', minWidth: 120 }}>
            <div className="stat-value">{formatADG(adg.overall)}</div>
            <div className="stat-label">Avg Daily Gain</div>
          </div>
          <div className="stat-card" style={{ flex: '1', minWidth: 120 }}>
            <div className="stat-value">{formatADG(adg.recent)}</div>
            <div className="stat-label">Recent Gain</div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="inline-form mt-3">
          <div className="form-grid form-grid-3">
            <div className="field">
              <label>Weight (kg)</label>
              <input type="number" step="0.1" value={form.weight_kg} onChange={e => set('weight_kg', e.target.value)} placeholder="e.g. 45.5" />
            </div>
            <div className="field">
              <label>Date</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div className="field">
              <label>Type</label>
              <select value={form.weight_type} onChange={e => set('weight_type', e.target.value)}>
                <option value="birth">Birth</option>
                <option value="weaning">Weaning</option>
                <option value="periodic">Periodic</option>
              </select>
            </div>
          </div>
          <div className="form-actions mt-3">
            <button className="btn btn-secondary btn-sm" onClick={closeForm}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSubmit}>{editing ? 'Save Changes' : 'Save'}</button>
          </div>
        </div>
      )}

      {weights.length === 0 ? (
        <p className="text-muted mt-2">No weight records yet.</p>
      ) : (
        <>
          {weights.length >= 2 && (
            <div className="chart-panel">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#d7e0d5" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} unit=" kg" />
                  <Tooltip formatter={(v) => [`${v} kg`, 'Weight']} />
                  <Line type="monotone" dataKey="kg" stroke="#2f7d4c" strokeWidth={2} dot={{ fill: '#2f7d4c', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="table-wrap mt-2">
            <table>
              <thead><tr><th>Date</th><th>Weight (kg)</th><th>Type</th><th></th></tr></thead>
              <tbody>
                {[...weights].reverse().map(w => (
                  <tr key={w.id}>
                    <td data-label="Date">{w.date}</td>
                    <td data-label="Weight"><strong>{w.weight_kg} kg</strong></td>
                    <td data-label="Type" style={{ textTransform: 'capitalize' }}>{w.weight_type}</td>
                    <td data-label="Actions">
                      <div className="row-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => startEdit(w)}>Edit</button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => window.confirm('Delete this weight record?') && handleDelete(w.id)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
